import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import {
    Table,
    Spin,
    DatePicker,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import CalendarIcon from "../../assets/img/svg/xCalendaricon.svg?react";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import "./reports.css";
import { exportToExcel } from "../exporttoexcel/exporttoexcel";
import ReportModal from "./report-modal";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { CSVLink } from "react-csv";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";
import validationService from "../../components/commonvalidation/validationService";

const FullExtractReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const { roleUserId } = useAuth();
    const { accessToken } = useAuthToken();
    const [filteredData, setFilteredData] = useState([]);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [startfloatLabel, setStartfloatLabel] = useState("");
    const [endfloatLabel, setEndfloatLabel] = useState("");
    const [isbn, setIsbn] = useState("");
    const [bid, setBid] = useState("");
    const [errors, setErrors] = useState({});
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalBody, setModalBody] = useState("");
    const csvLinkRef = useRef(null);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const hasIsbnOrBid = (isbn && isbn.trim() !== "") || (bid && bid.trim() !== "");
    useEffect(() => {
        const loadRegexPatterns = async () => {
            try {
                await validationService.fetchRegexData(accessToken);
            } catch (error) {
                console.error("Failed to load regex patterns:", error);
            }
        };

        if (accessToken) {
            loadRegexPatterns();
        }
    }, [accessToken]);
    const getInputClass = (value, baseClass = "book-page-form-input-text") => {
        const hasValue =
            value !== null &&
            value !== undefined &&
            (typeof value === "number" || String(value).trim() !== "");

        return `${baseClass}${hasValue ? " has-text" : ""}`;
    };

    const handleIsbnChange = (e) => {
        setIsbn(e.target.value);
        setIsFormChanged(true);
        if (errors.isbn) {
            setErrors((prev) => ({ ...prev, isbn: null }));
        }
        setErrors((prev) => ({ ...prev, startdate: null, enddate: null }))
    };

    const handleBidChange = (e) => {
        setBid(e.target.value);
        setIsFormChanged(true);
        if (errors.bid) {
            setErrors((prev) => ({ ...prev, bid: null }));
        }
        setErrors((prev) => ({ ...prev, startdate: null, enddate: null }))
    };

    const handleAutoFetch = () => {
        if (!validate()) {
            const firstErrorField = Object.keys(errors)[0];
            if (firstErrorField) {
                const element = document.getElementById(firstErrorField);
                if (element) element.focus();
            }
            return;
        }

        const resetPagination = { ...pagination, current: 1 };
        setPagination(resetPagination);
        fetchData({
            pagination: resetPagination,
            sorter: sortedInfo,
            filters: searchFilters
        });
    };

    const handleExport_new = async () => {
        if (!validate()) {
            console.log("Validation Failed:", errors);
            return;
        }
        const startdate = selectedStartDate
            ? selectedStartDate.format("YYYY-MM-DD")
            : null;
        const enddate = selectedEndDate
            ? selectedEndDate.format("YYYY-MM-DD")
            : null;

        const payload = {
            startDate: startdate,
            endDate: enddate,
            reportID: 11,
            userIndex: roleUserId,
            userTimeZone: "string",
            operatorID: "string",
            operatorName: "string",
            qcRework: "string",
            qcStatus: "string",
            pageNo: 1,
            pageSize: -1,
            search: "string",
            orderByColumn: "string",
            orderBy: "string",
            reportName: "Full Extract Report",
            isbn: isbn || "",
            bid: bid || "",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetReportData, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(
                        responseText || "Failed to parse API response."
                    );
                }
            }

            const books = Array.isArray(result) ? result : [result];
            if (
                books[0]?.fullExtractReport?.[0]?.reportDirectDownload &&
                books[0]?.fullExtractReport?.[0]?.reportMessage?.trim()
            ) {
                setModalTitle("Report Processing Initiated");
                setModalBody(books[0].fullExtractReport[0].reportMessage);
                setReportModalVisible(true);
                setIsFormChanged(false);
            } else {
                const formattedData = books[0]?.fullExtractReport || [];

                if (formattedData.length === 0) {
                    message.error("The grid is currently empty. There are no records to export.");
                    setData([]);
                    setFilteredData([]);
                    setPagination((prev) => ({
                        ...prev,
                        current: 1,
                        total: 0,
                    }));
                    setIsFormChanged(false);
                    return;
                }

                const dynamicFileName = buildTimestampedFileName("FullExtractReport");
                exportToExcel(formattedData, columns, dynamicFileName);
                setIsFormChanged(false);
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        // 1. Global Requirement Check
        if (!hasIsbnOrBid && !selectedStartDate && !selectedEndDate) {
            message.warning("Please provide at least one search criteria: Date Range, ISBN, or BID.");
            return false;
        }

        const newErrors = {};
        // 2. Date Validation (Flattened)
        if (selectedStartDate || selectedEndDate) {
            if (!selectedStartDate) {
                newErrors.startdate = "Please select start date.";
            } else if (!selectedEndDate) {
                newErrors.enddate = "Please select end date.";
            } else if (new Date(selectedEndDate) < new Date(selectedStartDate)) {
                newErrors.datevalidate = "End Date cannot be lesser than Start Date";
            }
        }

        // 3. BID Validation
        if (bid?.trim()) {
            const bidError = validationService.validateBid(bid);
            if (bidError) newErrors.bid = bidError;
        }

        // 4. ISBN Validation
        if (isbn?.trim()) {
            const isbnResult = validationService.validateISBN(isbn);
            if (!isbnResult.isValid) {
                newErrors.isbn = 'Please enter a valid 10 or 13-digit ISBN.' || isbnResult.error;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStartDateChange = (date) => {
        setSelectedStartDate(date);
        setIsFormChanged(true);

        if (date !== null) {
            setStartfloatLabel("float-label");
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.startdate;
                return newErrors;
            });
        } else {
            setStartfloatLabel("");
        }
    };

    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };

    const handleEndDateChange = (date) => {
        setSelectedEndDate(date);
        setIsFormChanged(true);

        if (date !== null) {
            setEndfloatLabel("float-label");
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.enddate;
                return newErrors;
            });
        } else {
            setEndfloatLabel("");
        }
    };

    const handleCleardate = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartfloatLabel("");
        setEndfloatLabel("");
        setIsbn("");
        setBid("");
        setData([]);
        setFilteredData([]);
        setIsFormChanged(false);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));
        setErrors({});
    };

    const handleDownloadClick = async () => {
        if (!validate()) {
            focusFirstErrorField();
            return;
        }

        await performDownload();
    };

    const focusFirstErrorField = () => {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
            const element = document.getElementById(firstErrorField);
            if (element) element.focus();
        }
    };

    const performDownload = async () => {
        setLoading(true);

        const payload = buildPayload();

        try {
            const responseData = await fetchReportData(payload);
            const processedData = processResponseData(responseData);

            if (processedData.length === 0) {
                message.error("The grid is currently empty. There are no records to export.");
                setData([]);
                setFilteredData([]);
                setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    total: 0,
                }));
                setIsFormChanged(false);
                return false;
            }

            setFilteredData(processedData);
            setIsFormChanged(false);
            triggerCsvDownload();
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false);
        }
    };

    const buildPayload = () => {
        const startdate = selectedStartDate
            ? selectedStartDate.format("YYYY-MM-DD")
            : null;
        const enddate = selectedEndDate
            ? selectedEndDate.format("YYYY-MM-DD")
            : null;

        return {
            startDate: startdate,
            endDate: enddate,
            reportID: 11,
            userIndex: roleUserId,
            userTimeZone: "string",
            operatorID: "string",
            operatorName: "string",
            qcRework: "string",
            qcStatus: "string",
            pageNo: 1,
            pageSize: -1,
            search: "string",
            orderByColumn: "string",
            orderBy: "string",
            reportName: "Full Extract Report",
            isbn: isbn || "",
            bid: bid || "",
        };
    };

    const fetchReportData = async (payload) => {
        const myHeaders = {
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
            "Content-Type": "application/json",
        };

        const response = await fetch(CommonObj.Endpoints.GetReportData, {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const responseText = await response.text();
        return parseResponseText(responseText);
    };

    const parseResponseText = (responseText) => {
        try {
            const result = JSON.parse(responseText);
            return responseText.includes("No data found") ? [] : result;
        } catch (e) {
            if (responseText.includes("No data found")) {
                return [];
            }
            throw new Error(
                responseText || "Failed to parse API response."
            );
        }
    };

    const processResponseData = (result) => {
        const books = Array.isArray(result) ? result : [result];
        const getBooks = books[0]?.fullExtractReport
            ? books[0].fullExtractReport
            : [];

        return getBooks.map((row) => ({
            ...row,
            isbN10: `\t${row.isbN10}`,
            isbN13: `\t${row.isbN13}`,
            isbn: `\t${row.isbn}`,
            createdOn: `\t${row.createdOn}`,
            weight: `\t${row.weight}`
        }));
    };

    const triggerCsvDownload = () => {
        setTimeout(() => {
            if (csvLinkRef.current) {
                csvLinkRef.current.link.click();
            }
        }, 0);
    };

    const convertDateFormat = (date) => {
        const formattedDate = date
            ? date.replace(/^(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
            : "";
        return formattedDate;
    };

    const columns = [
        {
            title: "BID",
            dataIndex: "bid",
            key: "bid   ",
            width: 160,
        },
        {
            title: "Rack-Shelf",
            dataIndex: "rackShelf",
            key: "rackShelf",
            width: 200,
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 400,
        },
        {
            title: "ISBN",
            dataIndex: "isbn",
            key: "isbn",
            width: 200,
        },
        {
            title: "ISBN-10",
            dataIndex: "isbN10",
            key: "isbN10",
            width: 220,
        },
        {
            title: "ISBN-13",
            dataIndex: "isbN13",
            key: "isbN13",
            width: 220,
        },
        {
            title: "Author",
            dataIndex: "author",
            key: "author",
            width: 220,
        },
        {
            title: "Publisher",
            dataIndex: "publisher",
            key: "publisher",
            width: 220,
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 220,
        },
        {
            title: "Book Cover URL",
            dataIndex: "bookCoverURL",
            key: "bookCoverURL",
            width: 200,
        },
        {
            title: "Book Type",
            dataIndex: "bookType",
            key: "bookType",
            width: 220,
        },
        {
            title: "Binding",
            dataIndex: "binding",
            key: "binding",
            width: 220,
        },
        {
            title: "Exception Reason",
            dataIndex: "exceptionReason",
            key: "exceptionReason",
            width: 200,
        },
        {
            title: "EQComplexityLevel",
            dataIndex: "eqComplexityLevel",
            key: "eqComplexityLevel",
            width: 200,
        },
        {
            title: "Pages",
            dataIndex: "pages",
            key: "pages",
            width: 200,
        },
        {
            title: "Updated Page Count",
            dataIndex: "updatedPageCount",
            key: "updatedPageCount",
            width: 200,
        },
        {
            title: "Condition",
            dataIndex: "condition",
            key: "condition",
            width: 200,
        },
        {
            title: "Used Condition",
            dataIndex: "usedCondition",
            key: "usedCondition",
            width: 200,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 200,
        },
        {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            width: 200,
        },
        {
            title: "Dimension Height",
            dataIndex: "dimensionHeight",
            key: "dimensionHeight",
            width: 200,
        },
        {
            title: "Dimension Length",
            dataIndex: "dimensionLength",
            key: "dimensionLength",
            width: 200,
        },
        {
            title: "Content",
            dataIndex: "content",
            key: "content",
            width: 200,
        },
        {
            title: "Text",
            dataIndex: "text",
            key: "text",
            width: 200,
        },
        {
            title: "Full Bleed",
            dataIndex: "fullBleed",
            key: "fullBleed",
            width: 200,
        },
        {
            title: "Images",
            dataIndex: "images",
            key: "images",
            width: 200,
        },
        {
            title: "Graphs",
            dataIndex: "graphs",
            key: "graphs",
            width: 200,
        },
        {
            title: "Maps",
            dataIndex: "maps",
            key: "maps",
            width: 200,
        },
        {
            title: "CreatedOn",
            dataIndex: "createdON",
            key: "createdON",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "User ID",
            dataIndex: "intakeUserID",
            key: "intakeUserID",
            width: 200,
        },
        {
            title: "User Name",
            dataIndex: "intakeUserName",
            key: "intakeUserName",
            width: 200,
        },
        {
            title: "Intake Start Date & Time",
            dataIndex: "intakeStartDateandTime",
            key: "intakeStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Intake End Date & Time",
            dataIndex: "intakeEndDateandTime",
            key: "intakeEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Intake Total Time",
            dataIndex: "intakeTotalTime",
            key: "intakeTotalTime",
            width: 200,
        },
        {
            title: "Intake Reject Reason",
            dataIndex: "intakeRejectReason",
            key: "intakeRejectReason",
            width: 200,
        },
        {
            title: "User ID",
            dataIndex: "cuttingUserID",
            key: "cuttingUserID",
            width: 200,
        },
        {
            title: "User Name",
            dataIndex: "cuttingUserName",
            key: "cuttingUserName",
            width: 200,
        },
        {
            title: "Cutting Start Date & Time",
            dataIndex: "cuttingStartDateandTime",
            key: "cuttingStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Cutting End Date & Time",
            dataIndex: "cuttingEndDateandTime",
            key: "cuttingEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Cutting Total Time",
            dataIndex: "cuttingTotalTime",
            key: "cuttingTotalTime",
            width: 200,
        },
        {
            title: "Cutting Reject Reason",
            dataIndex: "cuttingRejectReason",
            key: "cuttingRejectReason",
            width: 200,
        },
        {
            title: "Scanning Hardcover Outside User ID",
            dataIndex: "scanningHardcoverOutSideUserID",
            key: "scanningHardcoverOutSideUserID",
            width: 200,
        },
        {
            title: "Scanning Hardcover Outside User Name",
            dataIndex: "scanningHardcoverOutSideUserName",
            key: "scanningHardcoverOutSideUserName",
            width: 200,
        },
        {
            title: "Scanning Hardcover Outside Start Date & Time",
            dataIndex: "scanningHardcoverOutSideStartDateandTime",
            key: "scanningHardcoverOutSideStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning Hardcover Outside End Date & Time",
            dataIndex: "scanningHardcoverOutSideEndDateandTime",
            key: "scanningHardcoverOutSideEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning Hardcover Outside Total Time",
            dataIndex: "scanningHardcoverOutSideTotalTime",
            key: "scanningHardcoverOutSideTotalTime",
            width: 200,
        },
        {
            title: "Scanning Hardcover Inside User Id",
            dataIndex: "scanningHardcoverInsideUserID",
            key: "scanningHardcoverInsideUserID",
            width: 200,
        },
        {
            title: "Scanning Hardcover Inside User Name",
            dataIndex: "scanningHardcoverInsideUserName",
            key: "scanningHardcoverInsideUserName",
            width: 200,
        },
        {
            title: "Scanning Hardcover Inside Start Date & Time",
            dataIndex: "scanningHardcoverInsideStartDateandTime",
            key: "scanningHardcoverInsideStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning Hardcover Inside End Date & Time",
            dataIndex: "scanningHardcoverInsideEndDateandTime",
            key: "scanningHardcoverInsideEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning Hardcover Inside Total Time",
            dataIndex: "scanningHardcoverInsideTotalTime",
            key: "scanningHardcoverInsideTotalTime",
            width: 200,
        },
        {
            title: "Scanning Paperback UserId",
            dataIndex: "scanningPaperBagUserID",
            key: "scanningPaperBagUserID",
            width: 200,
        },
        {
            title: "Scanning PaperBack UserName",
            dataIndex: "scanningPaperBagUserName",
            key: "scanningPaperBagUserName",
            width: 200,
        },
        {
            title: "Scanning PaperBack Start Date & Time",
            dataIndex: "scanningPaperBagStartDateandTime",
            key: "scanningPaperBagStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning PaperBack End Date & Time",
            dataIndex: "scanningPaperBagEndDateandTime",
            key: "scanningPaperBagEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Scanning PaperBack Total Time",
            dataIndex: "scanningPaperBagTotalTime",
            key: "scanningPaperBagTotalTime",
            width: 200,
        },
        {
            title: "Scanning Action",
            dataIndex: "scanningAction",
            key: "scanningAction",
            width: 200,
        },
        {
            title: "Scanning Action Rework Reason",
            dataIndex: "scanningActionReworkReason",
            key: "scanningActionReworkReason",
            width: 200,
        },
        {
            title: "Scanning Action Rejection Reason",
            dataIndex: "scanningActionRejectionReason",
            key: "scanningActionRejectionReason",
            width: 300,
        },
        {
            title: "User ID",
            dataIndex: "qcUserID",
            key: "qcUserID",
            width: 200,
        },
        {
            title: "User Name",
            dataIndex: "qcUserName",
            key: "qcUserName",
            width: 200,
        },
        {
            title: "QC Start Date & Time",
            dataIndex: "qcStartDateandTime",
            key: "qcStartDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "QC End Date & Time",
            dataIndex: "qcEndDateandTime",
            key: "qcEndDateandTime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "QC Total Time",
            dataIndex: "qcTotalTime",
            key: "qcTotalTime",
            width: 200,
        },
        {
            title: "QC Status",
            dataIndex: "qcStatus",
            key: "qcStatus",
            width: 200,
        },
        {
            title: "Error Category",
            dataIndex: "errorCategory",
            key: "errorCategory",
            width: 200,
        },
        {
            title: "Error Comments",
            dataIndex: "errorComments",
            key: "errorComments",
            width: 400,
        },
        {
            title: "Error Count",
            dataIndex: "errorCount",
            key: "errorCount",
            width: 200,
        },
        {
            title: "QC Action",
            dataIndex: "qcAction",
            key: "qcAction",
            width: 200,
        },
        {
            title: "QC Rework Comments",
            dataIndex: "qcReworkComments",
            key: "qcReworkComments",
            width: 200,
        },
        {
            title: "QC Rework Station",
            dataIndex: "qcReworkStation",
            key: "qcReworkStation",
            width: 200,
        },
        {
            title: "Delivery To S3",
            dataIndex: "deliveryToS3",
            key: "deliveryToS3",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Client Review",
            dataIndex: "clientReview",
            key: "clientReview",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Client Acceptance / Rejection",
            dataIndex: "clientAcceptance",
            key: "clientAcceptance",
            width: 200,
        },
        {
            title: "Client Reject Reason",
            dataIndex: "clientRejectionReason",
            key: "clientRejectionReason",
            width: 200,
        },
        {
            title: "Comments",
            dataIndex: "comments",
            key: "comments",
            width: 400,
        },
        {
            title: "Internal Rework",
            dataIndex: "internalRework",
            key: "internalRework",
            width: 200,
        },
        {
            title: "External Rework",
            dataIndex: "externalRework",
            key: "externalRework",
            width: 200,
        },
        {
            title: "Recycle Bin ID",
            dataIndex: "recycleBinId",
            key: "recycleBinId",
            width: 200,
        },
        {
            title: "GL# Timestamp",
            dataIndex: "glTimeStamp",
            key: "glTimeStamp",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Load ID",
            dataIndex: "loadId",
            key: "loadId",
            width: 200,
        },
        {
            title: "Shipped Date",
            dataIndex: "shippedDate",
            key: "shippedDate",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Shipped By",
            dataIndex: "shippedBy",
            key: "shippedBy",
            width: 200,
        },
        {
            title: "COD#",
            dataIndex: "cod",
            key: "cod",
            width: 200,
        },
        {
            title: "COD Date & Time",
            dataIndex: "codDatetime",
            key: "codDatetime",
            width: 200,
            render: (text) => {
                return <span>{convertDateFormat(text)}</span>;
            },
        },
        {
            title: "Auto Forwarded",
            dataIndex: "autoForwarded",
            key: "autoForwarded",
            width: 200,
            render: (text) => {
                return <span>{text}</span>;
            },
        },
        {
            title: "Vendor",
            dataIndex: "vendor",
            key: "vendor",
            width: 200,
        },
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const startdate = selectedStartDate
            ? selectedStartDate.format("YYYY-MM-DD")
            : null;
        const enddate = selectedEndDate
            ? selectedEndDate.format("YYYY-MM-DD")
            : null;

        const payload = {
            startDate: startdate,
            endDate: enddate,
            reportID: 11,
            userIndex: roleUserId,
            userTimeZone: "string",
            operatorID: "string",
            operatorName: "string",
            qcRework: "string",
            qcStatus: "string",
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: "string",
            orderByColumn: "string",
            orderBy: "string",
            reportName: "Full Extract Report",
            isFrontEnd: true,
            isbn: isbn || "",
            bid: bid || "",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetReportData, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(
                        responseText || "Failed to parse API response."
                    );
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books[0]?.fullExtractReport || [];
            if (formattedData.length === 0) {
                message.warning("No data found for the selected criteria.");
            }
            setData(formattedData);
            setIsFormChanged(false);

            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            setFilteredData(formattedData);
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false);
        }
    };


    if (loading) {
        return (
            <Spin
                size="large"
                style={{ margin: "20px auto", display: "block" }}
            />
        );
    }

    const isExportDisabled = data.length === 0 && !isFormChanged;
    return (
        <div className="active-page-container">
            <ReportModal
                title={modalTitle}
                modalBody={modalBody}
                Visible={reportModalVisible}
                setVisible={setReportModalVisible}
                queueURL="reportsdownloadqueue"
            />

            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Full Source Extract Report</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container">
                    </div>
                </div>
            </div>
            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg>
                        </span>
                        Select Date Range
                    </div>
                    <div className="advance-filter-action">
                        <button
                            style={{ display: "none" }}
                            className="active-page-header-button"
                        >
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        opacity="0.5"
                                        cx="11.5"
                                        cy="11.5"
                                        r="7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                    />
                                    <path
                                        d="M15 12H8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        <button
                            style={{ display: "none" }}
                            className="active-page-header-button"
                        >
                            <span>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12.0311"
                                        cy="11.9999"
                                        r="7.51943"
                                        stroke="#E93548"
                                    />
                                    <path
                                        d="M13.912 10.1192L10.1523 13.8789M10.1523 10.1191L13.912 13.8788"
                                        stroke="#E93548"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div className="date-range-info-label" style={{ paddingBottom: "6px", whiteSpace: "nowrap" }}>
                        Note : Viewing active production data since <strong>{dayjs().subtract(90, "day").format("MM/DD/YYYY")}</strong>
                    </div>
                </div>
                <div className="advance-filter-body-container">
                    <div className="advance-filter-input-container">
                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <DatePicker
                                    variant="underlined"
                                    placeholder=""
                                    className={`${"book-page-form-input-text"} ${selectedStartDate === null ? "" : "has-text"}`}
                                    suffixIcon={<CalendarIcon />}
                                    value={selectedStartDate}
                                    onChange={handleStartDateChange}
                                    disabledDate={disableFutureDates}
                                    allowClear={false}
                                    id="startdate-report-1"
                                />
                                <label
                                    className={startfloatLabel}
                                    htmlFor="startdate-report-1"
                                >
                                    Start Date{" "}
                                </label>
                                {errors.startdate && (
                                    <span className="error-msg">
                                        {errors.startdate}
                                    </span>
                                )}
                                {errors.datevalidate && (
                                    <span className="error-msg-1">
                                        {errors.datevalidate}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <DatePicker
                                    variant="underlined"
                                    placeholder=""
                                    className={`${"book-page-form-input-text"} ${selectedEndDate === null ? "" : "has-text"}`}
                                    suffixIcon={<CalendarIcon />}
                                    value={selectedEndDate}
                                    onChange={handleEndDateChange}
                                    disabledDate={disableFutureDates}
                                    allowClear={false}
                                    id="enddate-report-1"
                                />
                                <label
                                    htmlFor="enddate-report-1"
                                    className={endfloatLabel}
                                >
                                    End Date{" "}
                                </label>
                                {errors.enddate && (
                                    <span className="error-msg">
                                        {errors.enddate}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <input
                                    id="isbn"
                                    name="isbn"
                                    className={getInputClass(isbn)}
                                    value={isbn}
                                    onChange={handleIsbnChange}
                                    onBlur={handleAutoFetch}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAutoFetch();
                                    }}
                                />
                                <label htmlFor="isbn" className="label">
                                    ISBN
                                </label>
                                {errors.isbn && (
                                    <span className="error-msg">{errors.isbn}</span>
                                )}
                            </div>
                        </div>

                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <input
                                    id="bid"
                                    name="bid"
                                    className={getInputClass(bid)}
                                    value={bid}
                                    onChange={handleBidChange}
                                    onBlur={handleAutoFetch}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAutoFetch();
                                    }}
                                />
                                <label htmlFor="bid" className="label">
                                    BID
                                </label>
                                {errors.bid && (
                                    <span className="error-msg">{errors.bid}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="advance-filter-button-container">
                        <button
                            className="cancel-button-gray-outline"
                            onClick={handleCleardate}
                        >
                            Clear
                        </button>
                        <button
                            className="submit-button-gradiant-outline"
                            data-track="fullextract-report-generate"
                            onClick={(e) => {
                                e.preventDefault();
                                if (!validate()) {
                                    const firstErrorField = Object.keys(errors)[0];
                                    if (firstErrorField) {
                                        const element = document.getElementById(firstErrorField);
                                        if (element) element.focus();
                                    }
                                    return;
                                }

                                const resetPagination = {
                                    ...pagination,
                                    current: 1,
                                };
                                setPagination(resetPagination);
                                fetchData({
                                    pagination: resetPagination,
                                    sorter: sortedInfo,
                                    filters: searchFilters,
                                });
                            }}
                        >
                            Generate
                        </button>
                        <button
                            className="submit-button-gradiant-outline"
                            data-track="reports-full-extract-export-csv"
                            disabled={true}
                            style={{ cursor: "not-allowed", display: "inline-block" }}
                            onClick={(e) => {
                                e.preventDefault();
                                if (!validate()) {
                                    const firstErrorField = Object.keys(errors)[0];
                                    if (firstErrorField) {
                                        const element = document.getElementById(firstErrorField);
                                        if (element) element.focus();
                                    }
                                    return;
                                }
                                handleExport_new();
                            }}
                        >
                            Export as CSV
                        </button>

                        <button
                            className="submit-button-gradiant-outline"
                            data-track="reports-full-extract-export-csv-download"
                            onClick={handleDownloadClick}
                            disabled={loading || isExportDisabled}
                            style={{ display: "none" }}
                        >
                            {loading ? "Processing..." : "Export as CSV"}
                        </button>
                        <CSVLink
                            data={filteredData}
                            headers={columns
                                .filter(
                                    (col) =>
                                        col && col.title && col.dataIndex
                                )
                                .map((col) => ({
                                    label: col.title,
                                    key: col.dataIndex,
                                }))}
                            filename={"FullExtractReport.csv"}
                            style={{ display: "none" }}
                            ref={csvLinkRef}
                        />
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.bid}
                        onChange={handleTableChange}
                        scroll={{
                            x: "max-content",
                            y: "calc(100vh - 280px)",
                        }}
                        rowClassName={getRowClassName}
                    />
                </div>
            </div>
        </div>
    );
};
export default FullExtractReport;